import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { menuPOAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Trash2, Search, Filter,
  ChevronUp, ChevronDown, Download, Star, Clock,
  AlertTriangle, CheckCircle, Package, ShoppingCart,
  ChefHat, X, Loader2, Eye, ArrowUpDown, History,
  UtensilsCrossed, Repeat2, Leaf
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const CATEGORIES = [
  'Proteins - Non Veg', 'Proteins - Veg', 'Dairy', 'Vegetables',
  'Fruits', 'Lentils & Legumes', 'Rice & Grains', 'Flour & Baking',
  'Spices & Condiments', 'Oils & Fats', 'Sweets & Desserts',
  'Beverages', 'Snacks & Street Food', 'Miscellaneous'
];

const PRIORITY_CONFIG = {
  URGENT: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', icon: '🔴', label: 'Urgent' },
  WEEKLY: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: '🟡', label: 'Weekly' },
  BULK: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', icon: '🟢', label: 'Bulk' },
};

const CHART_COLORS = [
  '#1B3A5C', '#2E6DA4', '#E53E3E', '#D69E2E', '#38A169',
  '#805AD5', '#DD6B20', '#3182CE', '#E53E3E', '#319795',
  '#D53F8C', '#718096', '#2D3748', '#4A5568'
];

function UploadZone({ onFilesSelected, isProcessing }) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFilesSelected(files);
  }, [onFilesSelected]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  return (
    <div
      data-testid="upload-zone"
      className={`
        border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
        ${dragOver ? 'border-[#2E6DA4] bg-blue-50/50' : 'border-slate-300 hover:border-[#2E6DA4] hover:bg-slate-50'}
        ${isProcessing ? 'pointer-events-none opacity-60' : ''}
      `}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".xlsx,.xls,.csv,.pdf,.txt,.doc,.docx"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files);
          if (files.length) onFilesSelected(files);
          e.target.value = '';
        }}
      />
      {isProcessing ? (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 text-[#2E6DA4] animate-spin" />
          <p className="text-[#1B3A5C] font-semibold text-lg">Analyzing menus with AI...</p>
          <p className="text-slate-500 text-sm">Extracting dishes, mapping ingredients, categorizing...</p>
          <div className="flex gap-2 mt-2">
            {['Parsing files', 'Mapping ingredients', 'Categorizing'].map((step, i) => (
              <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.3}s` }}>
                {step}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-full bg-[#1B3A5C]/10 flex items-center justify-center">
            <Upload className="h-7 w-7 text-[#1B3A5C]" />
          </div>
          <div>
            <p className="text-[#1B3A5C] font-semibold text-lg">Drop event menu files here</p>
            <p className="text-slate-500 text-sm mt-1">Excel (.xlsx, .xls), PDF, CSV, or Text files</p>
          </div>
          <Button variant="outline" className="mt-2 border-[#2E6DA4] text-[#2E6DA4] hover:bg-[#2E6DA4] hover:text-white">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Browse Files
          </Button>
        </div>
      )}
    </div>
  );
}

function KPICards({ summary }) {
  const cards = [
    { label: 'Total Events', value: summary.total_events || 0, icon: ShoppingCart, color: 'text-[#1B3A5C]', bg: 'bg-[#1B3A5C]/10' },
    { label: 'Total Ingredients', value: summary.total_ingredients || 0, icon: Package, color: 'text-[#2E6DA4]', bg: 'bg-[#2E6DA4]/10' },
    { label: 'Urgent Items', value: summary.urgent_items || 0, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Weekly Items', value: summary.weekly_items || 0, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Bulk Items', value: summary.bulk_items || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-cards">
      {cards.map((c, i) => (
        <Card key={i} className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}>
              <c.icon className={`h-5 w-5 ${c.color}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1B3A5C]">{c.value}</p>
              <p className="text-xs text-slate-500">{c.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EventCards({ events }) {
  if (!events?.length) return null;
  return (
    <div className="flex flex-wrap gap-3" data-testid="event-cards">
      {events.map((evt, i) => (
        <Card key={i} className="border-[#2E6DA4]/30 bg-gradient-to-br from-white to-blue-50/30 flex-1 min-w-[200px]">
          <CardContent className="p-4">
            <p className="font-semibold text-[#1B3A5C] text-sm">{evt.name}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              <Badge variant="outline" className="text-xs">{evt.date || 'TBD'}</Badge>
              <Badge variant="outline" className="text-xs">Pax: {evt.pax || 'TBD'}</Badge>
            </div>
            {evt.meals?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {evt.meals.map((m, j) => (
                  <span key={j} className="text-[10px] bg-[#1B3A5C]/10 text-[#1B3A5C] px-2 py-0.5 rounded-full">{m}</span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function FilterPanel({ filters, setFilters, categories, priorities, eventNames }) {
  return (
    <div className="space-y-4" data-testid="filter-panel">
      <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Search</label>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            data-testid="search-input"
            placeholder="Search ingredient..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="pl-8 h-9 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600 mb-2 block">Priority</label>
        <div className="flex flex-wrap gap-1.5">
          {['ALL', 'URGENT', 'WEEKLY', 'BULK'].map(p => (
            <button
              key={p}
              data-testid={`priority-filter-${p.toLowerCase()}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                filters.priority === p
                  ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#2E6DA4]'
              }`}
              onClick={() => setFilters(f => ({ ...f, priority: p }))}
            >
              {p === 'ALL' ? 'All' : `${PRIORITY_CONFIG[p]?.icon || ''} ${PRIORITY_CONFIG[p]?.label || p}`}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600 mb-2 block">Categories</label>
        <div className="max-h-[280px] overflow-y-auto space-y-1 pr-1">
          {categories.map(cat => (
            <label key={cat} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded">
              <input
                type="checkbox"
                checked={filters.categories.includes(cat)}
                onChange={(e) => {
                  setFilters(f => ({
                    ...f,
                    categories: e.target.checked
                      ? [...f.categories, cat]
                      : f.categories.filter(c => c !== cat)
                  }));
                }}
                className="rounded border-slate-300"
              />
              <span className="text-slate-700 truncate">{cat}</span>
            </label>
          ))}
        </div>
      </div>

      {eventNames.length > 1 && (
        <div>
          <label className="text-xs font-medium text-slate-600 mb-2 block">Events</label>
          <div className="space-y-1">
            {eventNames.map(evt => (
              <label key={evt} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded">
                <input
                  type="checkbox"
                  checked={filters.events.includes(evt)}
                  onChange={(e) => {
                    setFilters(f => ({
                      ...f,
                      events: e.target.checked
                        ? [...f.events, evt]
                        : f.events.filter(ev => ev !== evt)
                    }));
                  }}
                  className="rounded border-slate-300"
                />
                <span className="text-slate-700 truncate">{evt}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <Button
        variant="ghost"
        size="sm"
        className="w-full text-xs text-slate-500"
        onClick={() => setFilters({ search: '', priority: 'ALL', categories: [], events: [] })}
      >
        Clear All Filters
      </Button>
    </div>
  );
}

function POTable({ data, eventNames, sortConfig, onSort }) {
  const totalEvents = eventNames.length;

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-3 py-2.5 text-left text-xs font-semibold text-white cursor-pointer hover:bg-[#2E6DA4] transition-colors select-none ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortConfig.field === field ? (
          sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </div>
    </th>
  );

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    data.forEach(item => {
      const cat = item.category || 'Miscellaneous';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [data]);

  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="po-table">
      <table className="w-full text-sm">
        <thead className="bg-[#1B3A5C] sticky top-0 z-10">
          <tr>
            <SortHeader field="sno" className="w-12">S.No</SortHeader>
            <SortHeader field="ingredient">Ingredient</SortHeader>
            <SortHeader field="unit" className="w-16">Unit</SortHeader>
            <SortHeader field="estimated_qty" className="w-20">Est. Qty</SortHeader>
            {eventNames.map(evt => (
              <th key={evt} className="px-2 py-2.5 text-center text-xs font-semibold text-white w-20 hidden md:table-cell">
                <span className="truncate block max-w-[80px]" title={evt}>{evt}</span>
              </th>
            ))}
            <SortHeader field="event_count" className="w-16">Events</SortHeader>
            <SortHeader field="priority" className="w-20">Priority</SortHeader>
            <th className="px-3 py-2.5 text-left text-xs font-semibold text-white min-w-[220px]">Usage Notes (Dishes)</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(grouped).map(([category, items]) => (
            <React.Fragment key={category}>
              <tr>
                <td
                  colSpan={6 + eventNames.length}
                  className="bg-[#1B3A5C]/90 text-white font-bold text-xs px-3 py-2 uppercase tracking-wide"
                >
                  {category} ({items.length})
                </td>
              </tr>
              {items.map((item, idx) => {
                const pconf = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.BULK;
                const allEvents = item.event_count >= totalEvents && totalEvents > 1;
                return (
                  <tr key={idx} className={`${pconf.bg} border-b border-slate-100 hover:brightness-95 transition-all`}>
                    <td className="px-3 py-2 text-xs text-slate-500">{item.sno}</td>
                    <td className="px-3 py-2 text-sm font-medium text-[#1B3A5C]">
                      <span className="flex items-center gap-1.5">
                        {item.ingredient}
                        {allEvents && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{item.unit}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 font-medium">{item.estimated_qty || 'TBD'}</td>
                    {eventNames.map(evt => (
                      <td key={evt} className="px-2 py-2 text-center hidden md:table-cell">
                        {item.events?.[evt] ? (
                          <span className="text-emerald-600 font-bold text-xs">✔</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center">
                      <span className="text-xs font-semibold text-[#1B3A5C]">{item.event_count}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pconf.badge}`}>
                        {pconf.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[260px]" title={item.usage_notes || item.notes || ''}>
                      <span className="line-clamp-2">{item.usage_notes || item.notes || ''}</span>
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No ingredients match your filters</p>
        </div>
      )}
    </div>
  );
}

function CategoryChart({ data }) {
  const chartData = useMemo(() => {
    const counts = {};
    data.forEach(item => {
      const cat = item.category || 'Miscellaneous';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [data]);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#1B3A5C]">Ingredients by Category</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" paddingAngle={2}>
              {chartData.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v, name) => [`${v} items`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 gap-1 mt-2 max-h-[120px] overflow-y-auto">
          {chartData.map((d, i) => (
            <div key={d.name} className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="truncate">{d.name}</span>
              <span className="ml-auto font-semibold">{d.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PriorityChart({ data }) {
  const chartData = useMemo(() => {
    const counts = { URGENT: 0, WEEKLY: 0, BULK: 0 };
    data.forEach(item => { counts[item.priority] = (counts[item.priority] || 0) + 1; });
    return [
      { name: 'Urgent', value: counts.URGENT, fill: '#E53E3E' },
      { name: 'Weekly', value: counts.WEEKLY, fill: '#D69E2E' },
      { name: 'Bulk', value: counts.BULK, fill: '#38A169' },
    ];
  }, [data]);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#1B3A5C]">Items by Priority</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={60} />
            <Tooltip />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function CrossEventItems({ data, totalEvents }) {
  const topItems = useMemo(() => {
    return [...data]
      .filter(i => i.event_count > 1)
      .sort((a, b) => b.event_count - a.event_count)
      .slice(0, 5);
  }, [data]);

  if (!topItems.length) return null;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#1B3A5C]">Top Cross-Event Ingredients</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {topItems.map((item, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-700">
              {item.event_count >= totalEvents && <Star className="h-3 w-3 text-amber-500 fill-amber-400" />}
              {item.ingredient}
            </span>
            <Badge variant="outline" className="text-[10px]">{item.event_count}/{totalEvents} events</Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const COURSE_COLORS = {
  'Starter': 'bg-orange-100 text-orange-700',
  'Main Course': 'bg-blue-100 text-blue-700',
  'Bread': 'bg-amber-100 text-amber-700',
  'Rice': 'bg-yellow-100 text-yellow-700',
  'Dessert': 'bg-pink-100 text-pink-700',
  'Beverage': 'bg-teal-100 text-teal-700',
  'Soup': 'bg-emerald-100 text-emerald-700',
  'Accompaniment': 'bg-slate-100 text-slate-700',
  'Live Counter': 'bg-purple-100 text-purple-700',
  'Hi Tea': 'bg-rose-100 text-rose-700',
  'Snack': 'bg-lime-100 text-lime-700',
};

function DishesTable({ dishes, eventNames, dishSearch, setDishSearch, dishCourseFilter, setDishCourseFilter, dishSortConfig, onDishSort }) {
  const totalEvents = eventNames.length;
  const courses = useMemo(() => [...new Set((dishes || []).map(d => d.course))].sort(), [dishes]);

  const filteredDishes = useMemo(() => {
    let result = [...(dishes || [])];
    if (dishSearch) {
      const s = dishSearch.toLowerCase();
      result = result.filter(d => d.name?.toLowerCase().includes(s) || d.course?.toLowerCase().includes(s));
    }
    if (dishCourseFilter) {
      result = result.filter(d => d.course === dishCourseFilter);
    }
    result.sort((a, b) => {
      const f = dishSortConfig.field;
      const dir = dishSortConfig.dir === 'asc' ? 1 : -1;
      if (f === 'event_count') return ((a.event_count || 0) - (b.event_count || 0)) * dir;
      return String(a[f] || '').localeCompare(String(b[f] || '')) * dir;
    });
    return result;
  }, [dishes, dishSearch, dishCourseFilter, dishSortConfig]);

  // Group by course
  const grouped = useMemo(() => {
    const groups = {};
    filteredDishes.forEach(d => {
      const c = d.course || 'Other';
      if (!groups[c]) groups[c] = [];
      groups[c].push(d);
    });
    return groups;
  }, [filteredDishes]);

  const repeatedCount = useMemo(() => (dishes || []).filter(d => d.event_count > 1).length, [dishes]);

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-3 py-2.5 text-left text-xs font-semibold text-white cursor-pointer hover:bg-[#2E6DA4] transition-colors select-none ${className}`}
      onClick={() => onDishSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {dishSortConfig.field === field ? (
          dishSortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="dishes-section">
      {/* Dishes summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1B3A5C]/10 flex items-center justify-center">
              <UtensilsCrossed className="h-5 w-5 text-[#1B3A5C]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1B3A5C]">{(dishes || []).length}</p>
              <p className="text-xs text-slate-500">Total Dishes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Repeat2 className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{repeatedCount}</p>
              <p className="text-xs text-slate-500">Repeated Across Events</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Leaf className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{(dishes || []).filter(d => d.type === 'Veg').length}</p>
              <p className="text-xs text-slate-500">Veg Dishes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
              <UtensilsCrossed className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{(dishes || []).filter(d => d.type === 'Non-Veg').length}</p>
              <p className="text-xs text-slate-500">Non-Veg Dishes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            data-testid="dish-search-input"
            placeholder="Search dish name..."
            value={dishSearch}
            onChange={(e) => setDishSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        <select
          data-testid="dish-course-filter"
          className="h-9 rounded-md border border-slate-200 text-sm px-3 bg-white"
          value={dishCourseFilter}
          onChange={(e) => setDishCourseFilter(e.target.value)}
        >
          <option value="">All Courses</option>
          {courses.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-slate-500">
          Showing {filteredDishes.length} of {(dishes || []).length} dishes
        </span>
      </div>

      {/* Dishes Table */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="dishes-table">
        <table className="w-full text-sm">
          <thead className="bg-[#1B3A5C] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-white w-8">#</th>
              <SortHeader field="name">Dish Name</SortHeader>
              <SortHeader field="course" className="w-28">Course</SortHeader>
              <th className="px-3 py-2.5 text-center text-xs font-semibold text-white w-16">Type</th>
              {eventNames.map(evt => (
                <th key={evt} className="px-2 py-2.5 text-center text-xs font-semibold text-white w-24">
                  <span className="truncate block max-w-[90px]" title={evt}>{evt}</span>
                </th>
              ))}
              <SortHeader field="event_count" className="w-24">Appears In</SortHeader>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-white">Key Ingredients</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([course, courseDishes]) => (
              <React.Fragment key={course}>
                <tr>
                  <td
                    colSpan={5 + eventNames.length}
                    className="bg-[#1B3A5C]/90 text-white font-bold text-xs px-3 py-2 uppercase tracking-wide"
                  >
                    {course} ({courseDishes.length})
                  </td>
                </tr>
                {courseDishes.map((dish, idx) => {
                  const isRepeated = dish.event_count > 1;
                  return (
                    <tr
                      key={`${course}-${idx}`}
                      className={`${isRepeated ? 'bg-amber-50/60' : 'bg-white'} border-b border-slate-100 hover:bg-slate-50 transition-all`}
                    >
                      <td className="px-3 py-2 text-xs text-slate-400">{idx + 1}</td>
                      <td className="px-3 py-2 text-sm font-medium text-[#1B3A5C]">
                        <span className="flex items-center gap-1.5">
                          {dish.name}
                          {isRepeated && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${COURSE_COLORS[dish.course] || 'bg-slate-100 text-slate-600'}`}>
                          {dish.course}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          dish.type === 'Veg' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {dish.type}
                        </span>
                      </td>
                      {eventNames.map(evt => (
                        <td key={evt} className="px-2 py-2 text-center">
                          {dish.events?.[evt] ? (
                            <span className="text-emerald-600 font-bold text-xs">&#10004;</span>
                          ) : (
                            <span className="text-slate-300">&mdash;</span>
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isRepeated ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {dish.event_count}/{totalEvents} event{dish.event_count > 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 max-w-[250px]">
                        <span className="truncate block" title={dish.key_ingredients?.join(', ')}>
                          {dish.key_ingredients?.slice(0, 4).join(', ')}
                          {dish.key_ingredients?.length > 4 && ` +${dish.key_ingredients.length - 4} more`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filteredDishes.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <UtensilsCrossed className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No dishes match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MenuPOAssistantPage() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [pastAnalyses, setPastAnalyses] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({ search: '', priority: 'ALL', categories: [], events: [] });
  const [sortConfig, setSortConfig] = useState({ field: 'sno', dir: 'asc' });
  const [activeTab, setActiveTab] = useState('ingredients');
  const [dishSearch, setDishSearch] = useState('');
  const [dishCourseFilter, setDishCourseFilter] = useState('');
  const [dishSortConfig, setDishSortConfig] = useState({ field: 'name', dir: 'asc' });

  const pollingRef = useRef(null);

  useEffect(() => {
    loadPastAnalyses();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const loadPastAnalyses = async () => {
    try {
      const res = await menuPOAPI.listAnalyses();
      setPastAnalyses(res.data || []);
    } catch { /* ignore */ }
  };

  const startPolling = useCallback((analysisId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await menuPOAPI.getAnalysis(analysisId);
        const data = res.data;
        if (data.status === 'active') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setCurrentAnalysis(data);
          setIsProcessing(false);
          toast.success(`Analysis complete — ${data?.summary?.total_ingredients || 0} ingredients, ${data?.dishes?.length || 0} dishes found`);
          loadPastAnalyses();
        } else if (data.status === 'error') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setIsProcessing(false);
          toast.error(data.error || 'Analysis failed');
        }
        // else still processing, keep polling
      } catch {
        // ignore transient errors during polling
      }
    }, 5000);
  }, []);

  const handleFilesSelected = async (files) => {
    setIsProcessing(true);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    try {
      const res = await menuPOAPI.analyze(formData);
      const data = res.data;
      if (data.status === 'processing') {
        // Start polling for results
        setCurrentAnalysis(data);
        setShowHistory(false);
        setFilters({ search: '', priority: 'ALL', categories: [], events: [] });
        setActiveTab('ingredients');
        setDishSearch('');
        setDishCourseFilter('');
        toast.info('Processing menus with AI... This may take 30-60 seconds.');
        startPolling(data.id);
      } else {
        // Already complete (unlikely in async flow)
        setCurrentAnalysis(data);
        setIsProcessing(false);
        setShowHistory(false);
        toast.success('Analysis complete!');
        loadPastAnalyses();
      }
    } catch (err) {
      setIsProcessing(false);
      toast.error(err.response?.data?.detail || 'Failed to start menu analysis');
    }
  };

  const handleViewAnalysis = async (id) => {
    try {
      const res = await menuPOAPI.getAnalysis(id);
      setCurrentAnalysis(res.data);
      setShowHistory(false);
      setFilters({ search: '', priority: 'ALL', categories: [], events: [] });
      setActiveTab('ingredients');
      setDishSearch('');
      setDishCourseFilter('');
    } catch {
      toast.error('Failed to load analysis');
    }
  };

  const handleDelete = async (id) => {
    try {
      await menuPOAPI.deleteAnalysis(id);
      toast.success('Analysis deleted');
      if (currentAnalysis?.id === id) setCurrentAnalysis(null);
      loadPastAnalyses();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleExportExcel = async () => {
    if (!currentAnalysis) return;
    try {
      const res = await menuPOAPI.exportExcel({
        purchase_order: currentAnalysis.purchase_order,
        summary: currentAnalysis.summary
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Kitchen_PO_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const summary = currentAnalysis?.summary || {};
  const purchaseOrder = currentAnalysis?.purchase_order || [];
  const dishes = currentAnalysis?.dishes || [];
  const eventNames = useMemo(() => (summary.events || []).map(e => e.name), [summary]);
  const usedCategories = useMemo(() => [...new Set(purchaseOrder.map(i => i.category))], [purchaseOrder]);

  // Apply filters
  const filteredData = useMemo(() => {
    let result = [...purchaseOrder];
    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(i => i.ingredient?.toLowerCase().includes(s) || i.notes?.toLowerCase().includes(s));
    }
    if (filters.priority !== 'ALL') {
      result = result.filter(i => i.priority === filters.priority);
    }
    if (filters.categories.length) {
      result = result.filter(i => filters.categories.includes(i.category));
    }
    if (filters.events.length) {
      result = result.filter(i => filters.events.some(ev => i.events?.[ev]));
    }
    // Sort
    result.sort((a, b) => {
      const f = sortConfig.field;
      const dir = sortConfig.dir === 'asc' ? 1 : -1;
      const av = a[f] ?? '';
      const bv = b[f] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return result;
  }, [purchaseOrder, filters, sortConfig]);

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDishSort = (field) => {
    setDishSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div className="space-y-5 p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="menu-po-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1B3A5C] flex items-center justify-center">
            <ChefHat className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1B3A5C]">Menu PO Assistant</h1>
            <p className="text-xs text-slate-500">AI-powered ingredient extraction from event menus</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="history-btn"
            className="text-xs"
            onClick={() => setShowHistory(!showHistory)}
          >
            <History className="h-3.5 w-3.5 mr-1" />
            History ({pastAnalyses.length})
          </Button>
          {currentAnalysis && (
            <Button
              size="sm"
              data-testid="export-excel-btn"
              className="bg-[#1B3A5C] hover:bg-[#1B3A5C]/90 text-white text-xs"
              onClick={handleExportExcel}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Export to Excel
            </Button>
          )}
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Past Analyses</span>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pastAnalyses.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No past analyses yet</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {pastAnalyses.map(a => (
                  <div key={a.id} className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${a.status === 'processing' ? 'border-blue-200 bg-blue-50/50' : 'border-slate-100 hover:bg-slate-50'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1B3A5C] truncate flex items-center gap-2">
                        {a.files?.map(f => f.filename).join(', ') || 'Unknown Files'}
                        {a.status === 'processing' && <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />}
                        {a.status === 'error' && <AlertTriangle className="h-3 w-3 text-red-500" />}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {new Date(a.created_at).toLocaleString()} — {a.status === 'processing' ? 'Processing...' : a.status === 'error' ? 'Failed' : `${a.summary?.total_ingredients || 0} ingredients, ${a.summary?.total_events || 0} events`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button variant="ghost" size="sm" onClick={() => handleViewAnalysis(a.id)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(a.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Zone (always visible) */}
      <UploadZone onFilesSelected={handleFilesSelected} isProcessing={isProcessing} />

      {/* Warning if few ingredients */}
      {currentAnalysis && purchaseOrder.length > 0 && purchaseOrder.length < 3 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="warning-banner">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="text-sm text-amber-700">Only {purchaseOrder.length} ingredient(s) extracted. The menu file may not have been parsed correctly.</span>
        </div>
      )}

      {/* Dashboard - only when we have analysis */}
      {currentAnalysis && currentAnalysis.status === 'processing' && (
        <Card className="border-[#2E6DA4]/30">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-10 w-10 text-[#2E6DA4] animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1B3A5C]">Analyzing menus with AI...</h3>
            <p className="text-sm text-slate-500 mt-2">Extracting dishes, mapping ingredients, categorizing, and estimating quantities.</p>
            <p className="text-xs text-slate-400 mt-1">This typically takes 30-60 seconds. The page will update automatically.</p>
            <div className="flex justify-center gap-2 mt-4">
              {['Parsing files', 'Extracting dishes', 'Mapping ingredients', 'Categorizing'].map((step, i) => (
                <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.4}s` }}>
                  {step}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {currentAnalysis && currentAnalysis.status === 'error' && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-700">Analysis Failed</h3>
            <p className="text-sm text-red-600 mt-1 max-w-lg mx-auto">{currentAnalysis.error || 'An unexpected error occurred. Please try uploading again.'}</p>
            <div className="flex gap-2 justify-center mt-4">
              <Button variant="outline" size="sm" onClick={() => { setCurrentAnalysis(null); setIsProcessing(false); }}>
                Upload New Files
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {currentAnalysis && currentAnalysis.status === 'active' && (
        <>
          {/* Event Cards */}
          <EventCards events={summary.events} />

          {/* KPI Cards */}
          <KPICards summary={summary} />

          {/* Tab switcher */}
          <div className="flex items-center gap-1 border-b border-slate-200" data-testid="tab-switcher">
            <button
              data-testid="tab-ingredients"
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'ingredients'
                  ? 'border-[#1B3A5C] text-[#1B3A5C]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('ingredients')}
            >
              <span className="flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5" />
                Ingredients ({purchaseOrder.length})
              </span>
            </button>
            <button
              data-testid="tab-dishes"
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'dishes'
                  ? 'border-[#1B3A5C] text-[#1B3A5C]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => setActiveTab('dishes')}
            >
              <span className="flex items-center gap-1.5">
                <UtensilsCrossed className="h-3.5 w-3.5" />
                Dishes ({dishes.length})
                {dishes.filter(d => d.event_count > 1).length > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-semibold">
                    {dishes.filter(d => d.event_count > 1).length} repeated
                  </span>
                )}
              </span>
            </button>
          </div>

          {/* TAB: Ingredients (Purchase Order) */}
          {activeTab === 'ingredients' && (
            <>
            <div className="flex gap-4">
            {/* Filter sidebar */}
            <div className={`${showFilters ? 'w-[220px] min-w-[220px]' : 'w-0 min-w-0 overflow-hidden'} transition-all hidden lg:block`}>
              <Card className="border-slate-200 sticky top-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-semibold text-[#1B3A5C] flex items-center justify-between">
                    <span className="flex items-center gap-1"><Filter className="h-3 w-3" /> Filters</span>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => setShowFilters(false)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <FilterPanel
                    filters={filters}
                    setFilters={setFilters}
                    categories={usedCategories}
                    priorities={['URGENT', 'WEEKLY', 'BULK']}
                    eventNames={eventNames}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Main table area */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Mobile filter toggle + count */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {!showFilters && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowFilters(true)}>
                      <Filter className="h-3 w-3 mr-1" /> Filters
                    </Button>
                  )}
                  <span className="text-xs text-slate-500">
                    Showing {filteredData.length} of {purchaseOrder.length} items
                  </span>
                </div>
                {/* Mobile search */}
                <div className="relative lg:hidden w-48">
                  <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                    className="pl-7 h-8 text-xs"
                  />
                </div>
              </div>

              {/* Mobile priority filters */}
              <div className="flex flex-wrap gap-1.5 lg:hidden">
                {['ALL', 'URGENT', 'WEEKLY', 'BULK'].map(p => (
                  <button
                    key={p}
                    className={`text-xs px-2 py-1 rounded-full border transition-all ${
                      filters.priority === p
                        ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]'
                        : 'bg-white text-slate-600 border-slate-200'
                    }`}
                    onClick={() => setFilters(f => ({ ...f, priority: p }))}
                  >
                    {p === 'ALL' ? 'All' : `${PRIORITY_CONFIG[p]?.icon || ''} ${p}`}
                  </button>
                ))}
              </div>

              <POTable data={filteredData} eventNames={eventNames} sortConfig={sortConfig} onSort={handleSort} />
            </div>

            {/* Right sidebar: Charts */}
            <div className="w-[260px] min-w-[260px] space-y-4 hidden xl:block">
              <CategoryChart data={purchaseOrder} />
              <PriorityChart data={purchaseOrder} />
              <CrossEventItems data={purchaseOrder} totalEvents={eventNames.length} />
            </div>
          </div>

          {/* Mobile charts (below table) */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
            <CategoryChart data={purchaseOrder} />
            <PriorityChart data={purchaseOrder} />
            <CrossEventItems data={purchaseOrder} totalEvents={eventNames.length} />
          </div>
          </>
          )}

          {/* TAB: Dishes */}
          {activeTab === 'dishes' && (
            <DishesTable
              dishes={dishes}
              eventNames={eventNames}
              dishSearch={dishSearch}
              setDishSearch={setDishSearch}
              dishCourseFilter={dishCourseFilter}
              setDishCourseFilter={setDishCourseFilter}
              dishSortConfig={dishSortConfig}
              onDishSort={handleDishSort}
            />
          )}
        </>
      )}

      {/* Empty state when no analysis */}
      {!currentAnalysis && !isProcessing && (
        <div className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-[#1B3A5C]/5 flex items-center justify-center mx-auto mb-4">
            <ChefHat className="h-10 w-10 text-[#1B3A5C]/30" />
          </div>
          <h3 className="text-lg font-semibold text-[#1B3A5C]">Upload event menus to get started</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
            Upload one or more event menu files (Excel, PDF, or text) and the AI will extract all ingredients,
            categorize them, and generate a consolidated purchase order.
          </p>
        </div>
      )}
    </div>
  );
}
